import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useRef } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { API_CONFIGURED } from '../apiBase';
import { ApiHouseholdClient } from './ApiHouseholdClient';
import type { HouseholdDataClient } from './householdClient';
import { LocalHouseholdClient } from './LocalHouseholdClient';

const HouseholdDataContext = createContext<HouseholdDataClient | null>(null);

/**
 * Chooses the builder's persistence seam.
 *
 * SECURITY (hard-rules #1–4): client financial data must only ever be persisted
 * through the RLS-protected API, never the browser.
 *
 * - A configured API (`VITE_API_URL`) → `ApiHouseholdClient`, sending the auth
 *   session's bearer token so every read/write is RLS-scoped. This is the real
 *   path (works for local API dev, staging, and production).
 * - No API URL + dev build → the in-browser `LocalHouseholdClient` (real engine,
 *   `localStorage`), a DEV/DEMO convenience only.
 * - No API URL + production build → `ApiHouseholdClient` with no token, which
 *   fails closed rather than silently writing financials to the browser
 *   (CLAUDE.md: a dev-only weakening "cannot be enabled in production").
 */
function defaultHouseholdClient(getToken: () => string | null): HouseholdDataClient {
  if (API_CONFIGURED) return new ApiHouseholdClient(getToken);
  if (import.meta.env.DEV) return new LocalHouseholdClient();
  return new ApiHouseholdClient(() => null);
}

export function HouseholdDataProvider({
  client,
  children,
}: {
  client?: HouseholdDataClient;
  children: ReactNode;
}): JSX.Element {
  const { getToken } = useAuth();
  const ref = useRef<HouseholdDataClient>(client ?? defaultHouseholdClient(getToken));
  return (
    <HouseholdDataContext.Provider value={ref.current}>{children}</HouseholdDataContext.Provider>
  );
}

export function useHouseholdData(): HouseholdDataClient {
  const ctx = useContext(HouseholdDataContext);
  if (ctx === null) {
    throw new Error('useHouseholdData must be used inside HouseholdDataProvider');
  }
  return ctx;
}
